import * as RoutesModule from 'Routes';

import { createContext, useContext } from 'react';

import App from './App';
import { AppErrorBoundary } from 'components/ui/AppErrorBoundary';

// extentions
import './extentions/StringExtentions'

import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import type { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { BrowserRouter } from 'react-router-dom';
import { ConfigurationService } from 'services/ConfigurationService';
import ConfirmDiscardingChangesDialogStore from 'stores/ConfirmDiscardingChangesDialogStore';
import { InviteService } from 'services/InviteService';
import { InviteStore } from 'stores/InviteStore';
import { NotificationsStore } from 'stores/NotificationsStore';
import { OrganizationService } from 'services/OrganizationsService';
import { OrganizationsStore } from 'stores/OrganizationsStore';
import StartupService from 'services/StartupService';
import StartupStore from 'stores/StartupStore';
import { UsersService } from 'services/UsersService';
import { UsersStore } from 'stores/UsersStore';
import { createRoot } from 'react-dom/client';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import registerServiceWorker from './registerServiceWorker';
import { translationEn } from 'locales';
import { UserDiagnosticsStore } from 'stores/UserDiagnosticsStore';
import { UserDiagnosticsService } from 'services/UserDiagnosticsService';
import { TrackingDiagnosticsStore } from 'stores/TrackingDiagnosticsStore';
import { TrackingDiagnosticsService } from 'services/TrackingDiagnosticsService';
import { DiagnosticsPackagesStore } from 'stores/DiagnosticsPackagesStore';
import { DiagnosticsPackagesService } from 'services/DiagnosticsPackagesService';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

registerServiceWorker();

const langResources = {
  en: { translation: translationEn },
};

export const isLangAvailable = (lang: string) => {
  return Object.keys(langResources).includes(lang) ? lang : '';
};

type Stores = {
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  startupStore: StartupStore;
  confirmDiscardingChangesDialogStore: ConfirmDiscardingChangesDialogStore;
  organizationsStore: OrganizationsStore;
  usersStore: UsersStore;
  inviteStore: InviteStore;
  userDiagnosticStore: UserDiagnosticsStore;
  trackingDiagnosticsStore: TrackingDiagnosticsStore;
  diagnosticsPackagesStore: DiagnosticsPackagesStore;
};

const StoresContext = createContext<Stores | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

async function RenderApp() {
  // =========== Services ===========
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const authenticationService = new AuthenticationService();

  const serviceFactory = new AxiosServiceFactory(configurationService);
  const adminPortalApiInstance = await serviceFactory.createInstance();

  const organizationService = new OrganizationService(adminPortalApiInstance);
  const usersService = new UsersService(adminPortalApiInstance);
  const inviteService = new InviteService(adminPortalApiInstance);
  const userDiagnosticsService = new UserDiagnosticsService(adminPortalApiInstance);
  const trackingDiagnosticsService = new TrackingDiagnosticsService(adminPortalApiInstance);
  const diagnosticsPackagesService = new DiagnosticsPackagesService(adminPortalApiInstance);

  // =========== Stores ===========
  const notificationStore = new NotificationsStore();
  const organizationsStore = new OrganizationsStore(organizationService, notificationStore);
  const confirmDiscardingChangesDialogStore = new ConfirmDiscardingChangesDialogStore();
  const usersStore = new UsersStore(usersService, notificationStore, organizationsStore);
  const inviteStore = new InviteStore(inviteService, notificationStore, organizationsStore);
  const userDiagnosticsStore = new UserDiagnosticsStore(userDiagnosticsService, usersService, notificationStore);
  const trackingDiagnosticsStore = new TrackingDiagnosticsStore(trackingDiagnosticsService, notificationStore);
  const diagnosticsPackagesStore = new DiagnosticsPackagesStore(diagnosticsPackagesService, notificationStore);

  const startupService = new StartupService(adminPortalApiInstance);
  const startupStore = new StartupStore(authenticationService, configurationService, startupService, notificationStore);

  const stores: Stores = {
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance: adminPortalApiInstance,
    notificationStore,
    startupStore,
    organizationsStore,
    confirmDiscardingChangesDialogStore,
    usersStore,
    inviteStore,
    userDiagnosticStore: userDiagnosticsStore,
    trackingDiagnosticsStore: trackingDiagnosticsStore,
    diagnosticsPackagesStore: diagnosticsPackagesStore,
  };

  await i18next.use(initReactI18next).init({
    resources: langResources,
    lng: isLangAvailable(navigator.language.slice(0, 2)) || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

  AxiosServiceConfig.setDefaultDecorators(adminPortalApiInstance, authenticationService, errorHandler);

  root.render(
    <AppErrorBoundary>
      <BrowserRouter>
        <StoresContext.Provider value={stores}>
          <App>{RoutesModule.routes}</App>
        </StoresContext.Provider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

RenderApp();
